import { useQuery } from "@tanstack/react-query";
import { fetchDispensaryProfile } from "../lib/fetch-dispensary-profile";
import {
  fetchDispensaryDashboardMetricsBundle,
  fetchOrgDashboardMetricsBundle,
} from "../lib/dispensary-dashboard-api";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

export function useDispensaryDashboard() {
  const { dispensaryId, orgId, allLocations, getToken } = useDispensaryWorkspace();
  const aggregate = allLocations && orgId != null;
  const scopeKey = aggregate ? `org:${orgId}` : dispensaryId;
  const enabled = Boolean(dispensaryId) || aggregate;

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ["dispensary", "metrics", scopeKey],
    queryFn: async () => {
      if (aggregate) return fetchOrgDashboardMetricsBundle(getToken, orgId!);
      if (!dispensaryId) return null;
      return fetchDispensaryDashboardMetricsBundle(getToken, dispensaryId);
    },
    enabled,
    refetchInterval: 60_000,
  });

  // No single profile in "All locations" mode.
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["dispensary", "profile", dispensaryId],
    queryFn: () => {
      if (!dispensaryId) throw new Error("no_dispensary");
      return fetchDispensaryProfile(getToken, dispensaryId);
    },
    enabled: Boolean(dispensaryId) && !aggregate,
    staleTime: 5 * 60_000,
  });

  const isLoading = metricsLoading || (!aggregate && profileLoading);

  return {
    dispensaryName: aggregate ? "All locations" : (profile?.dbaName ?? profile?.legalName ?? "…"),
    activeTransporterCount: metrics?.activeTransporterCount ?? 0,
    ordersToday: metrics?.ordersToday ?? 0,
    ordersOnTheWay: metrics?.ordersOnTheWay ?? 0,
    avgDeliveryMinutes: metrics?.avgDeliveryMinutes ?? 0,
    pendingOrders: metrics?.pendingOrders ?? 0,
    ordersTodayDelta: metrics?.ordersTodayDelta ?? 0,
    onTheWayDelta: metrics?.onTheWayDelta ?? 0,
    pendingDelta: metrics?.pendingDelta ?? 0,
    isLoading,
    hasDispensary: enabled,
  };
}
